import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChartType } from 'chart.js';
import { Subscription } from 'rxjs';
import {
  AssetUnavailabilitySummary,
  AssetAvailability,
  UnavailableStatus,
  UnavailableStatusLabel,
  Customer,
} from 'src/app/core/models';
import {
  DefaultsService,
  AuthenticationService,
  EquipmentService,
  ProfileService,
} from 'src/app/core/providers';
import { NavService } from 'src/app/core/providers/services/nav.service';
import { SearchService } from 'src/app/core/providers/services/search.service';
import { ComponentBase } from '../component.base';
import { ChartOptions } from 'chart.js';
import { FiltersService } from '../../filters/filters.service';

@Component({
  selector: 'app-asset-unavailability',
  templateUrl: './asset-unavailability.component.html',
  styleUrls: ['./asset-unavailability.component.scss'],
  standalone: false,
})
export class AssetUnavailabilityComponent
  extends ComponentBase
  implements OnInit
{
  hasAssetUnavailabilitySubscription = false;
  assetUnavailabilitySummary?: AssetUnavailabilitySummary = undefined;
  categoryId: number;
  productTypeId: number;

  public assetAvailabilities: Array<AssetAvailability>;

  public pieChartLabels: string[] = [];
  public pieChartData: number[] = [];
  public pieChartType: ChartType = 'pie';
  public pieChartPlugins = [];
  public chartColors: Array<string> = [];
  public pieChartColors: any[] = [];
  public statusTypeUnavailable = 'Unavailable';
  public pieChartOptions: ChartOptions<'pie'> = {};
  public pieChartDatasets: any[] = [];
  public pieChartLegend = false;
  labelSuffix: string = ' %';
  assetLabel: string;

  /*constructor(
    public defaultsService: DefaultsService,
    public authenticationService: AuthenticationService,
    public equipmentService: EquipmentService,
    public profileService: ProfileService,
    public searchService: SearchService,
    public navService: NavService,
    public translateService: TranslateService,
    public route: ActivatedRoute,
    public router: Router,
    public filtersService: FiltersService
  ) {
    super(
      defaultsService,
      authenticationService,
      equipmentService,
      profileService,
      searchService,
      navService,
      translateService,
      route,
      router
    );
  }*/

  ngOnInit(): void {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    const categoryId = this.route.snapshot.paramMap.get('categoryId');
    const productTypeId = this.route.snapshot.paramMap.get('productTypeId');
    this.assetLabel = this.getLabelTranslation('Assets');
    this.statusTypeUnavailable = this.translateService.instant(
      'main.data.unavailable'
    );
    if (!categoryId || !productTypeId) {
      this.router.navigate(['/categories']);
    } else {
      this.subscriptions.push(
        this.profileService.selectedCustomersChanged.subscribe(
          (r: Array<Customer>) => {
            this.loadData();
          }
        )
      );
      this.categoryId = +categoryId;
      this.productTypeId = +productTypeId;
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
  }

  public loadData() {
    super.loadPageData();
    const assetUnavailabilitySummary =
      this.equipmentService.assetUnavailabilitySummary;
    if (!assetUnavailabilitySummary) {
      if (!this.hasAssetUnavailabilitySubscription) {
        this.subscriptions.push(
          this.equipmentService.assetUnavailabilitySummaryLoaded.subscribe(
            (avs) => {
              this.configureDataPoints(avs);
            }
          )
        );
        this.hasAssetUnavailabilitySubscription = true;
      }
      this.equipmentService.getAssetUnavailabilitySummary(
        this.categoryId,
        this.productTypeId
      );
    } else {
      this.configureDataPoints(assetUnavailabilitySummary);
    }
  }

  public configureDataPoints = (avs?: AssetUnavailabilitySummary) => {
    this.pieChartData = [];
    this.chartColors = [];
    this.pieChartLabels = [];

    if (avs) {
      this.assetAvailabilities = new Array<AssetAvailability>();
      avs.unavailability.forEach((r, i) => {
        this.assetAvailabilities.push({ ...r });
        this.pieChartData.push(r.asset_percent);
        this.chartColors.push(r.label_color);
        this.pieChartLabels.push(r.status_label + this.labelSuffix);
      });
      this.pieChartDatasets = [
        { data: this.pieChartData, backgroundColor: [...this.chartColors] },
      ];
      this.pieChartOptions = { responsive: true, maintainAspectRatio: false };
      this.assetUnavailabilitySummary = avs;
      this.isBusy = false;
    }
    this.authenticationService.finishSentryTransaction();
  };

  public chartClicked(e: any): void {
    if (e.active.length > 0) {
      const activeItem = e.active[0];
      if (activeItem) {
        let label: string =
          e.event.chart.legend.legendItems[activeItem.index].text;
        label = label.replace(this.labelSuffix, '');
        const assetAvailability =
          this.assetUnavailabilitySummary?.unavailability.find(
            (r) => r.status_label === label
          );
        if (assetAvailability) {
          this.openEquipmentAssetByStatus(assetAvailability.status_label);
        }
      }
    }
  }

  openAllEquipmentAssetStatuses = (
    assetSummary: AssetUnavailabilitySummary
  ) => {
    this.navService.showBackButton = true;
    this.navService.backButtonRoute = '/equipment-asset';
    this.navService.backButtonRouteParams = [
      this.categoryId.toString(),
      this.productTypeId.toString(),
    ];
    this.navService.pageTitle = `${this.navService.pageTitle} ${assetSummary.asset_description}`;
    this.router.navigate([
      '/equipment-asset',
      this.categoryId,
      this.productTypeId,
    ]);
  };

  openEquipmentAssetByStatus = (status: string | null) => {
    const statusType = status ? status : this.statusTypeUnavailable;
    this.equipmentService.equipmentAssets = undefined;
    this.navService.pageTitle = `${this.navService.pageTitle}`;
    if (statusType !== this.statusTypeUnavailable) {
      this.navService.pageTitle = `${this.navService.pageTitle.replace(this.statusTypeUnavailable, statusType)}`;
      this.router.navigate([
        'equipment-unavailable-asset',
        this.categoryId,
        this.productTypeId,
        this.statusTypeUnavailable.toLowerCase(),
        statusType,
      ]);
    } else {
      this.router.navigate([
        'equipment-unavailable-asset',
        this.categoryId,
        this.productTypeId,
        this.statusTypeUnavailable.toLowerCase(),
      ]);
    }
  };
}
